import { useContext, useEffect, useState } from "react";
import { getAgreementsRequest, getApplicationStatusDetailsRequest } from "@/services";
import { custom_context } from "@/utils/custom_context";
import {
  areRequiredAgreementsSigned,
  calculateStepStatus,
  getInitialActiveStep,
} from "../utils";
import { STEP_CONFIG } from "../constants";

/**
 * Fetches and holds application status for the current applicationId.
 * Updates ApplicationData context so children see the correct application.
 * Returns status, derived step state, and refetch handler.
 */
export function useApplicationStatus(applicationUuid) {
  const { setApplicationData } = useContext(custom_context.ApplicationData);
  const [applicationStatus, setApplicationStatus] = useState(undefined);
  const [kycDetails, setKycDetails] = useState({});
  const [activeStep, setActiveStep] = useState(undefined);
  const [agreementsDone, setAgreementsDone] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const fetchStatus = async () => {
    if (!applicationUuid) return;
    setLoadError(null);
    try {
      const response = await getApplicationStatusDetailsRequest({
        applicationId: applicationUuid,
      });
      if (response?.status === 200 && response?.results != null) {
        setApplicationData(applicationUuid, response.results);
        setApplicationStatus(response.results);
        setKycDetails(response.results?.kyc ?? {});

        let agreementsComplete = false;
        try {
          const agreementsResp = await getAgreementsRequest({
            loan_id: applicationUuid,
          });
          if (agreementsResp?.status) {
            agreementsComplete = areRequiredAgreementsSigned(
              agreementsResp.results || []
            );
          }
        } catch (err) {
          console.error("Error fetching agreements for initial step:", err);
        }

        setAgreementsDone(agreementsComplete);
        const stepStatus = calculateStepStatus(
          response.results,
          agreementsComplete
        );
        setActiveStep(getInitialActiveStep(response.results, stepStatus));
      } else {
        setApplicationData(applicationUuid, null);
        setApplicationStatus(undefined);
        setKycDetails({});
        setLoadError("Application not found.");
      }
    } catch (err) {
      console.error("Error fetching Application Status", err);
      setApplicationData(applicationUuid, null);
      setApplicationStatus(undefined);
      setKycDetails({});
      setLoadError(
        err?.response?.data?.message ??
        err?.message ??
        "Failed to load application. Please try again."
      );
    }
  };

  useEffect(() => {
    if (applicationUuid) fetchStatus();
  }, [applicationUuid]);

  const stepStatus = calculateStepStatus(applicationStatus, agreementsDone);
  const loanStatus = applicationStatus?.loan?.status || "incomplete";

  const goNext = () => {
    if (activeStep < STEP_CONFIG.ACCOUNT_SETUP) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const goBack = () => {
    if (activeStep > STEP_CONFIG.APPLICATION) {
      setActiveStep((prev) => prev - 1);
    }
  };

  return {
    applicationStatus,
    kycDetails,
    activeStep,
    setActiveStep,
    agreementsDone,
    setAgreementsDone,
    loadError,
    setLoadError,
    stepStatus,
    loanStatus,
    fetchStatus,
    goNext,
    goBack,
  };
}
